import { useState, useRef, useMemo } from 'react';
import abcjs from 'abcjs';
import {
  tunePlainText, setPlainText, sessionPlainText, bulkPlainText,
  tunePrintHtml, setPrintHtml, sessionPrintHtml, bulkPrintHtml,
  shareOrDownloadText, openPrintWindow,
  slugify, formatExportDate,
  buildArchiveZip, downloadZip,
} from '../utils/exportUtils';

/**
 * ExportModal — shared export UI for tunes, sets, sessions, and bulk selections.
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   type        — 'tune' | 'set' | 'session' | 'bulk'
 *   item        — tune | set | session object (null for bulk)
 *   tunes       — full tune objects:
 *                   type='set'     → allTunes array (used to resolve set's tune refs)
 *                   type='session' → ignored (session.tunes already hydrated)
 *                   type='bulk'    → the selected tune objects to export
 *                   type='tune'    → ignored
 */
export default function ExportModal({ open, onClose, type, item, tunes = [] }) {
  const [format, setFormat] = useState('text');   // 'text' | 'print' | 'archive'
  const [fullDetails, setFullDetails] = useState(true);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const abcRenderRef = useRef(null);

  // Resolve tunes for this export
  const resolvedTunes = useMemo(() => {
    if (type === 'tune') return item ? [item] : [];
    if (type === 'bulk') return tunes;
    if (type === 'session') return item?.tunes || [];
    if (type === 'set') {
      const refs = item?.tunes || [];
      return refs.map(ref => {
        const full = tunes.find(t => t.id === ref.id);
        return full || ref; // fall back to stub if offline
      });
    }
    return [];
  }, [type, item, tunes]);

  const title = useMemo(() => {
    if (type === 'tune') return item?.title || 'Tune';
    if (type === 'set') return item?.title || 'Set';
    if (type === 'session') return item?.location || 'Session';
    if (type === 'bulk') return `${tunes.length} ${tunes.length === 1 ? 'item' : 'items'}`;
    return 'Export';
  }, [type, item, tunes]);

  const filename = useMemo(() => {
    const date = new Date().toISOString().slice(0, 10);
    if (type === 'tune') return `${slugify(item?.title)}.txt`;
    if (type === 'set') return `set-${slugify(item?.title)}.txt`;
    if (type === 'session') {
      const d = item?.startedAt ? formatExportDate(item.startedAt).replace(/[^a-z0-9]+/gi, '-') : date;
      return `session-${slugify(item?.location)}-${d}.txt`;
    }
    return `allmytunes-export-${date}.txt`;
  }, [type, item, tunes]);

  // Build plain text for preview + share
  const textContent = useMemo(() => {
    if (!open) return '';
    if (type === 'tune') return tunePlainText(item);
    if (type === 'set') return setPlainText(item, resolvedTunes, { fullDetails });
    if (type === 'session') return sessionPlainText(item, { fullDetails });
    if (type === 'bulk') return bulkPlainText(resolvedTunes);
    return '';
  }, [open, type, item, resolvedTunes, fullDetails]);

  // Render all ABC notation to SVG strings using the hidden div ref
  function buildSvgMap(tunesToRender) {
    const map = {};
    const container = abcRenderRef.current;
    if (!container) return map;
    for (const tune of tunesToRender) {
      if (!tune.abc || tune.mode === 'notes') continue;
      try {
        container.innerHTML = '';
        abcjs.renderAbc(container, tune.abc, { staffwidth: 560, add_classes: true });
        const svg = container.querySelector('svg');
        if (svg) {
          map[tune.id] = svg.outerHTML;
        }
      } catch {
        // skip — will fall back to text ABC
      }
    }
    container.innerHTML = '';
    return map;
  }

  const handlePrint = () => {
    const svgMap = buildSvgMap(resolvedTunes);
    let html;
    if (type === 'tune') html = tunePrintHtml(item, svgMap[item?.id] || null);
    else if (type === 'set') html = setPrintHtml(item, resolvedTunes, svgMap, { fullDetails });
    else if (type === 'session') html = sessionPrintHtml(item, svgMap, { fullDetails });
    else html = bulkPrintHtml(resolvedTunes, svgMap);
    openPrintWindow(html);
  };

  const handleShare = async () => {
    await shareOrDownloadText(textContent, filename);
  };

  if (!open) return null;

  const showArchiveOption = type === 'bulk';

  const handleArchive = async () => {
    if (archiveBusy) return;
    setArchiveBusy(true);
    try {
      const bytes = await buildArchiveZip(resolvedTunes, { includeFiles });
      const date = new Date().toISOString().slice(0, 10);
      downloadZip(bytes, `allmytunes-archive-${date}.zip`);
    } catch (err) {
      console.error('Archive export failed:', err);
      alert('Archive export failed. Please try again.');
    } finally {
      setArchiveBusy(false);
    }
  };

  const showDetailToggle = (type === 'set' || type === 'session' || type === 'bulk') && format !== 'archive';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '400px',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Export</h3>
            <div style={{ fontSize: '0.82rem', opacity: 0.6, marginTop: '2px' }}>{title}</div>
          </div>
          <button className="btn-subtle" onClick={onClose} style={{ padding: '4px 10px', fontSize: '1.1rem' }}>✕</button>
        </div>

        {/* Format toggle */}
        <div style={{ display: 'flex', borderRadius: '8px', border: '1px solid var(--card-border, #ccc)', overflow: 'hidden', marginBottom: '1rem' }}>
          {[
            { key: 'text', label: 'Plain Text' },
            { key: 'print', label: 'Print / PDF' },
            ...(showArchiveOption ? [{ key: 'archive', label: 'Archive (ZIP)' }] : []),
          ].map((f, i, arr) => (
            <button
              key={f.key}
              onClick={() => setFormat(f.key)}
              style={{
                flex: 1, padding: '7px', fontSize: '0.85rem',
                fontWeight: format === f.key ? 700 : 400,
                backgroundColor: format === f.key ? 'var(--bg-card)' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: 'var(--text-primary)',
                borderRight: i < arr.length - 1 ? '1px solid var(--card-border, #ccc)' : 'none',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Include full details toggle (sets, sessions, bulk) */}
        {showDetailToggle && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', marginBottom: '1rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={fullDetails}
              onChange={e => setFullDetails(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Include full details for each {type === 'session' || type === 'bulk' ? 'tune' : 'item'}
          </label>
        )}

        {/* Plain text preview */}
        {format === 'text' && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.78rem', opacity: 0.5, marginBottom: '4px' }}>Preview</div>
            <pre style={{
              fontSize: '0.72rem',
              lineHeight: 1.5,
              backgroundColor: 'var(--input-bg)',
              borderRadius: '6px',
              padding: '0.6rem 0.75rem',
              maxHeight: '180px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
            }}>
              {textContent || '(nothing to export)'}
            </pre>
          </div>
        )}

        {/* Print hint */}
        {format === 'print' && (
          <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '1rem', lineHeight: 1.5 }}>
            Opens a formatted page with the print dialog. Choose <strong>Save as PDF</strong> or use the share icon in the print preview.
            {item?.abc || resolvedTunes.some(t => t.abc && t.mode !== 'notes')
              ? ' ABC notation will be rendered as music.'
              : ''}
          </p>
        )}

        {/* Archive hint + options */}
        {format === 'archive' && (
          <>
            <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '0.75rem', lineHeight: 1.5 }}>
              Downloads a <strong>.zip</strong> with one Markdown file per item (YAML frontmatter, backlinks) plus <strong>database.json</strong>.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', marginBottom: '1rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeFiles}
                onChange={e => setIncludeFiles(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              Include attached files &amp; recordings in <code>files/</code> folder
            </label>
          </>
        )}

        {/* Action button */}
        {format === 'text' ? (
          <button
            className="btn-primary"
            style={{ width: '100%', padding: '10px' }}
            onClick={handleShare}
            disabled={!textContent}
          >
            Share / Download .txt
          </button>
        ) : format === 'archive' ? (
          <button
            className="btn-primary"
            style={{ width: '100%', padding: '10px' }}
            onClick={handleArchive}
            disabled={archiveBusy || resolvedTunes.length === 0}
          >
            {archiveBusy
              ? (includeFiles ? 'Downloading & packaging files…' : 'Building archive…')
              : `Download .zip (${resolvedTunes.length} items)`}
          </button>
        ) : (
          <button
            className="btn-primary"
            style={{ width: '100%', padding: '10px' }}
            onClick={handlePrint}
          >
            Open Print Preview
          </button>
        )}

        {/* Off-screen div for abcjs rendering — needs real width so abcjs can reflow music */}
        <div ref={abcRenderRef} style={{ position: 'fixed', left: '-9999px', top: 0, width: '650px', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true" />
      </div>
    </div>
  );
}
